import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  ChevronLeft, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Stethoscope, 
  Shield, 
  Clock,
  CheckCircle2,
  Loader2,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { savePatientProfile, getPatientProfile, isPatientProfileComplete } from '@/utils/patientProfile';
import { getBudgetRangesForCountry } from '@/utils/budgetRanges';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE } from '@/config/api';
import { getAuthToken } from '@/utils/auth';
import { memberPath } from '@/config/hosts';
import BackToApp from '@/components/BackToApp';
import './get-started.css';

const API = `${API_BASE}/get-started`;

const cancerStages = ['Stage 0', 'Stage I', 'Stage II', 'Stage III', 'Stage IV', 'Not Diagnosed Yet'];
const contactMethods = ['Phone', 'Email', 'SMS'];
const timePreferences = ['Morning (9 AM - 12 PM)', 'Afternoon (12 PM - 5 PM)', 'Evening (5 PM - 8 PM)', 'Any Time'];

/** Dial codes with ISO labels (readable on Windows where flag emoji often collapse). */
const countryCodes = [
  { code: '+1', iso: 'US', country: 'United States' },
  { code: '+1', iso: 'CA', country: 'Canada' },
  { code: '+44', iso: 'GB', country: 'United Kingdom' },
  { code: '+91', iso: 'IN', country: 'India' },
  { code: '+61', iso: 'AU', country: 'Australia' },
  { code: '+65', iso: 'SG', country: 'Singapore' },
  { code: '+971', iso: 'AE', country: 'United Arab Emirates' },
  { code: '+81', iso: 'JP', country: 'Japan' },
  { code: '+82', iso: 'KR', country: 'South Korea' },
  { code: '+86', iso: 'CN', country: 'China' },
  { code: '+33', iso: 'FR', country: 'France' },
  { code: '+49', iso: 'DE', country: 'Germany' },
  { code: '+39', iso: 'IT', country: 'Italy' },
  { code: '+34', iso: 'ES', country: 'Spain' },
  { code: '+31', iso: 'NL', country: 'Netherlands' },
  { code: '+41', iso: 'CH', country: 'Switzerland' },
  { code: '+46', iso: 'SE', country: 'Sweden' },
  { code: '+47', iso: 'NO', country: 'Norway' },
  { code: '+60', iso: 'MY', country: 'Malaysia' },
  { code: '+66', iso: 'TH', country: 'Thailand' },
  { code: '+27', iso: 'ZA', country: 'South Africa' },
];

const SELECT_CONTENT =
  'by-select-content bg-cream-paper border-hair text-forest-ink p-1.5 max-h-72 overflow-y-auto rounded-2xl shadow-lg';
const SELECT_ITEM =
  'text-forest-ink cursor-pointer px-3 py-2.5 my-0.5 rounded-xl focus:bg-mist focus:text-forest-ink focus:outline-none data-[highlighted]:bg-mist data-[highlighted]:text-forest-ink';
const SELECT_TRIGGER =
  'h-12 bg-paper border-hair text-forest-ink rounded-2xl px-4 focus:ring-0 w-full';

function dialKey(iso, code) {
  return `${iso}:${code}`;
}

function splitPhone(raw = '') {
  const text = String(raw || '').trim();
  if (!text) return { country_key: dialKey('US', '+1'), country_code: '+1', phone: '' };
  const sorted = [...countryCodes].sort((a, b) => b.code.length - a.code.length);
  for (const row of sorted) {
    const escaped = row.code.replace('+', '\\+');
    const match = text.match(new RegExp(`^(${escaped})\\s*(.*)$`));
    if (match) {
      return {
        country_key: dialKey(row.iso, row.code),
        country_code: row.code,
        phone: (match[2] || '').replace(/\D/g, ''),
      };
    }
  }
  return {
    country_key: dialKey('US', '+1'),
    country_code: '+1',
    phone: text.replace(/\D/g, ''),
  };
}

function emptyForm(referralCancerName = '') {
  return {
    full_name: '',
    email: '',
    country_key: dialKey('US', '+1'),
    country_code: '+1',
    phone: '',
    city: '',
    state: '',
    country: 'United States',
    cancer_type: referralCancerName,
    cancer_stage: '',
    has_insurance: false,
    insurance_provider: '',
    insurance_policy_number: '',
    preferred_language: 'en',
    preferred_contact_method: 'phone',
    preferred_time: '',
    additional_notes: '',
    budget_range: '',
    budget_currency: '',
    preferred_cities: '',
    urgency: '',
    agree_to_terms: false,
    agree_to_contact: true,
  };
}

function applySubmissionToForm(base, submission, authUser) {
  const phoneParts = splitPhone(submission?.phone || authUser?.phone || '');
  const cities = Array.isArray(submission?.preferred_cities)
    ? submission.preferred_cities.join(', ')
    : (submission?.preferred_cities || '');
  return {
    ...base,
    full_name: submission?.full_name || authUser?.full_name || authUser?.name || base.full_name,
    email: authUser?.email || submission?.email || base.email,
    country_key: phoneParts.country_key || base.country_key,
    country_code: phoneParts.country_code || base.country_code,
    phone: phoneParts.phone || base.phone,
    city: submission?.city || authUser?.city || base.city,
    state: submission?.state || base.state,
    country: submission?.country || authUser?.country || base.country || 'United States',
    cancer_type: submission?.cancer_type || base.cancer_type,
    cancer_stage: submission?.cancer_stage || base.cancer_stage,
    has_insurance: Boolean(submission?.has_insurance),
    insurance_provider: submission?.insurance_provider || '',
    insurance_policy_number: submission?.insurance_policy_number || '',
    preferred_language: submission?.preferred_language || authUser?.preferred_language || 'en',
    preferred_contact_method: submission?.preferred_contact_method || 'phone',
    preferred_time: submission?.preferred_time || '',
    additional_notes: submission?.additional_notes || '',
    budget_range: submission?.budget_range_label || submission?.budget_range || '',
    budget_currency: submission?.budget_currency || '',
    preferred_cities: cities,
    urgency: submission?.urgency || '',
    agree_to_terms: Boolean(submission?.agree_to_terms),
    agree_to_contact: submission?.agree_to_contact !== false,
  };
}

function submissionLooksComplete(submission) {
  if (!submission) return false;
  return Boolean(
    submission.cancer_type &&
    submission.cancer_stage &&
    submission.city &&
    (submission.budget_range_label || submission.budget_range) &&
    ((Array.isArray(submission.preferred_cities) && submission.preferred_cities.length) || submission.preferred_cities) &&
    submission.urgency
  );
}

export default function GetStarted() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const referralParams = new URLSearchParams(location.search);
  const referralHospitalId = referralParams.get('hospital_id') || '';
  const referralCancerName = referralParams.get('cancer_name') || '';
  const forceEdit = referralParams.get('edit') === '1';
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [savedSubmission, setSavedSubmission] = useState(null);
  const [showForm, setShowForm] = useState(true);
  
  const [formData, setFormData] = useState(() => emptyForm(referralCancerName));
  const [errors, setErrors] = useState({});
  const selectedCountry =
    countryCodes.find((row) => dialKey(row.iso, row.code) === formData.country_key) ||
    countryCodes.find((row) => row.code === formData.country_code) ||
    countryCodes[0];

  const authHeaders = () => {
    const headers = {};
    const token = getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  };

  useEffect(() => {
    let live = true;
    (async () => {
      setBootLoading(true);
      try {
        let submission = null;
        let account = user || null;
        if (isAuthenticated) {
          const { data } = await axios.get(`${API}/me`, {
            withCredentials: true,
            headers: authHeaders(),
          });
          submission = data?.submission || null;
          account = data?.user || user || null;
          if (submission) {
            const local = {
              patientName: submission.full_name,
              isSelfPatient: true,
              relationToPatient: 'Self',
              cancerType: submission.cancer_type,
              cancerStage: submission.cancer_stage,
              city: submission.city,
              country: submission.country,
              preferredCities: submission.preferred_cities || [],
              budgetCurrency: submission.budget_currency || '',
              budgetRangeLabel: submission.budget_range_label || '',
              urgency: submission.urgency || null,
              phone: submission.phone || '',
              email: submission.email || account?.email || '',
              preferredLanguage: submission.preferred_language || account?.preferred_language || 'en',
              hasInsurance: Boolean(submission.has_insurance),
              insuranceProvider: submission.insurance_provider || '',
            };
            savePatientProfile(local);
          }
        } else {
          const local = getPatientProfile();
          if (isPatientProfileComplete(local)) {
            submission = {
              full_name: local.patientName,
              cancer_type: local.cancerType,
              cancer_stage: local.cancerStage,
              city: local.city,
              country: local.country,
              preferred_cities: local.preferredCities,
              budget_range_label: local.budgetRangeLabel,
              budget_currency: local.budgetCurrency,
              urgency: local.urgency,
            };
          }
        }

        if (!live) return;
        setSavedSubmission(submission);
        setFormData((prev) => applySubmissionToForm(
          { ...prev, cancer_type: referralCancerName || prev.cancer_type },
          submission,
          account,
        ));
        const complete = submissionLooksComplete(submission);
        setShowForm(forceEdit || !complete);
      } catch {
        if (!live) return;
        if (user) {
          setFormData((prev) => applySubmissionToForm(prev, null, user));
        }
        setShowForm(true);
      } finally {
        if (live) setBootLoading(false);
      }
    })();
    return () => { live = false; };
  }, [isAuthenticated, user?.id, user?.email, forceEdit, referralCancerName]);

  const savedSummary = useMemo(() => {
    const s = savedSubmission;
    if (!s) return null;
    const cities = Array.isArray(s.preferred_cities) ? s.preferred_cities.join(', ') : (s.preferred_cities || '');
    return {
      name: s.full_name || user?.full_name || '',
      email: s.email || user?.email || '',
      phone: s.phone || user?.phone || '',
      location: [s.city, s.state, s.country].filter(Boolean).join(', '),
      cancer: [s.cancer_type, s.cancer_stage].filter(Boolean).join(' · '),
      budget: s.budget_range_label || '',
      cities,
      urgency: s.urgency || '',
    };
  }, [savedSubmission, user]);

  const validateField = (name, value) => {
    switch (name) {
      case 'full_name':
        return value.length < 2 ? 'Full name must be at least 2 characters' : '';
      case 'email':
        return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Please enter a valid email' : '';
      case 'phone':
        const fullPhone = `${formData.country_code || '+1'}${value}`.replace(/\s+/g, '');
        return value.length < 10 || fullPhone.length < 12 ? 'Phone number must be at least 10 digits' : '';
      case 'city':
        return value.length < 2 ? 'City is required' : '';
      case 'cancer_type':
        return value.length < 2 ? 'Cancer type is required' : '';
      case 'cancer_stage':
        return !value ? 'Cancer stage is required' : '';
      case 'insurance_provider':
        return formData.has_insurance && !value ? 'Insurance provider is required if you have insurance' : '';
      case 'budget_range':
        return !value ? 'Budget range is required' : '';
      case 'preferred_cities':
        return !value || value.trim().length < 2 ? 'Please enter at least one preferred city' : '';
      case 'urgency':
        return !value ? 'Urgency of treatment is required' : '';
      default:
        return '';
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (name) => {
    const error = validateField(name, formData[name]);
    if (error) {
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Required fields
    const requiredFields = ['full_name', 'email', 'phone', 'city', 'cancer_type', 'cancer_stage', 'budget_range', 'preferred_cities', 'urgency'];
    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    // Conditional validation
    if (formData.has_insurance && !formData.insurance_provider) {
      newErrors.insurance_provider = 'Insurance provider is required';
      isValid = false;
    }

    if (!formData.agree_to_terms) {
      newErrors.agree_to_terms = 'You must agree to terms and conditions';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      setError('Please fill in all required fields correctly');
      return;
    }

    setLoading(true);

    try {
      // Parse preferred cities
      const preferredCitiesArray = formData.preferred_cities
        .split(',')
        .map(city => city.trim())
        .filter(city => city.length > 0);

      // Get budget info
      const budgetInfo = getBudgetRangesForCountry(formData.country);
      const selectedRange = budgetInfo.ranges.find(r => r.value === formData.budget_range);

      const submitData = {
        ...formData,
        phone: `${formData.country_code} ${formData.phone}`.trim(),
        preferred_cities: preferredCitiesArray,
        budget_currency: budgetInfo.currency,
        budget_range_label: formData.budget_range,
        urgency: formData.urgency,
        referral: referralHospitalId
          ? {
              hospital_id: referralHospitalId,
              cancer_name: referralCancerName || formData.cancer_type || '',
            }
          : undefined,
      };
      
      await axios.post(`${API}/submit`, submitData, {
        withCredentials: true,
        headers: authHeaders(),
      });
      
      // Save patient profile for Journey Builder + recommendations
      const patientProfile = {
        patientName: formData.full_name,
        isSelfPatient: true,
        relationToPatient: 'Self',
        cancerType: formData.cancer_type,
        cancerStage: formData.cancer_stage,
        city: formData.city,
        country: formData.country,
        preferredCities: preferredCitiesArray,
        budgetCurrency: budgetInfo.currency,
        budgetRangeLabel: formData.budget_range,
        urgency: formData.urgency,
        phone: `${formData.country_code} ${formData.phone}`.trim(),
        email: formData.email,
        preferredLanguage: formData.preferred_language || user?.preferred_language || 'en',
        hasInsurance: formData.has_insurance,
        insuranceProvider: formData.insurance_provider || '',
      };
      savePatientProfile(patientProfile);
      setSavedSubmission({
        ...submitData,
        preferred_cities: preferredCitiesArray,
        budget_range_label: formData.budget_range,
      });
      if (typeof refreshUser === 'function') {
        try { await refreshUser(); } catch (_) { /* ignore */ }
      }
      
      setSubmitted(true);
      
      // Handle returnUrl if present (redirect after successful submission)
      const urlParams = new URLSearchParams(location.search);
      const returnUrl = urlParams.get('returnUrl');
      if (returnUrl) {
        // Redirect after a short delay to show success message
        setTimeout(() => {
          navigate(returnUrl, { replace: true });
        }, 2000);
      } else if (isAuthenticated) {
        setTimeout(() => {
          navigate(memberPath('/'), { replace: true });
        }, 1600);
      }
    } catch (err) {
      // Normalize backend error shapes (string, object, or array) into a user-friendly string
      const detail = err?.response?.data?.detail;
      let message = 'Failed to submit form. Please try again.';

      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        // FastAPI validation errors often come as a list of objects
        const parts = detail
          .map((d) => d?.msg || d?.message || d?.detail || JSON.stringify(d))
          .filter(Boolean);
        if (parts.length > 0) {
          message = parts.join(', ');
        }
      } else if (detail && typeof detail === 'object') {
        message = detail.message || detail.error || detail.detail || JSON.stringify(detail);
      }

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (bootLoading) {
    return (
      <div className="gs-shell flex items-center justify-center p-4">
        <div className="flex items-center gap-3 text-forest-ink">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading your care details…</span>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="gs-shell flex items-center justify-center p-4">
        <div className="w-full max-w-2xl gs-main-card">
          <div className="p-6 sm:p-10 text-center">
              <div className="mb-6 flex justify-center">
                <div className="bg-mist border border-hair rounded-full h-20 w-20 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-forest-ink" />
                </div>
              </div>
              <h2 className="text-3xl font-semibold text-forest-ink mb-4" style={{ fontFamily: "'Bricolage Grotesque', 'Instrument Sans', system-ui, sans-serif" }}>
                Your care details are saved
              </h2>
              <p className="text-lg text-[#3d4a42] mb-8">
                We will remember these preferences on your next visit and use them to guide matching and recommendations.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate(isAuthenticated ? memberPath('/') : '/')}
                  className="bg-forest-ink hover:bg-forest-shadow text-cream-paper rounded-full px-8"
                >
                  {isAuthenticated ? 'Continue in the app' : 'Return to Home'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setSubmitted(false); setShowForm(false); }}
                  className="rounded-full px-8 border-hair"
                >
                  Review saved details
                </Button>
              </div>
          </div>
        </div>
      </div>
    );
  }

  if (!showForm && savedSummary) {
    return (
      <div className="gs-shell">
        <div className="gs-shell-inner" style={{ gridTemplateColumns: '1fr' }}>
          <main className="gs-main" style={{ maxWidth: 880, margin: '0 auto', width: '100%' }}>
          <div className="gs-back">
          <Button
            variant="ghost"
            className="text-forest-ink hover:text-charcoal px-2"
            onClick={() => navigate('/')}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Home
          </Button>
          </div>
          <div className="gs-main-card">
            <div className="mb-5">
              <h2 className="text-2xl text-forest-ink font-semibold" style={{ fontFamily: "'Bricolage Grotesque', 'Instrument Sans', system-ui, sans-serif" }}>
                Your care profile is on file
              </h2>
              <p className="text-[#3d4a42] text-base mt-1">
                We kept your details from last time. You do not need to start over. Update only if something changed.
              </p>
            </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-hair bg-mist/40 p-4">
                  <p className="text-xs uppercase tracking-wide text-[#6b7a72]">Patient</p>
                  <p className="mt-1 font-medium">{savedSummary.name || '-'}</p>
                  <p className="text-sm text-[#3d4a42]">{savedSummary.email}</p>
                  <p className="text-sm text-[#3d4a42]">{savedSummary.phone}</p>
                </div>
                <div className="rounded-2xl border border-hair bg-mist/40 p-4">
                  <p className="text-xs uppercase tracking-wide text-[#6b7a72]">Location</p>
                  <p className="mt-1 font-medium">{savedSummary.location || '-'}</p>
                </div>
                <div className="rounded-2xl border border-hair bg-mist/40 p-4">
                  <p className="text-xs uppercase tracking-wide text-[#6b7a72]">Diagnosis</p>
                  <p className="mt-1 font-medium">{savedSummary.cancer || '-'}</p>
                </div>
                <div className="rounded-2xl border border-hair bg-mist/40 p-4">
                  <p className="text-xs uppercase tracking-wide text-[#6b7a72]">Preferences</p>
                  <p className="mt-1 text-sm">Budget: {savedSummary.budget || '-'}</p>
                  <p className="text-sm">Cities: {savedSummary.cities || '-'}</p>
                  <p className="text-sm">Urgency: {savedSummary.urgency || '-'}</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-5">
                <Button
                  className="bg-forest-ink hover:bg-forest-shadow text-cream-paper rounded-full px-8"
                  onClick={() => navigate(isAuthenticated ? memberPath('/') : '/journey-builder')}
                >
                  {isAuthenticated ? 'Continue with these details' : 'Build a care package'}
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full px-8 border-hair"
                  onClick={() => setShowForm(true)}
                >
                  Update details
                </Button>
              </div>
          </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="gs-shell">
      <BackToApp />
      <style>{`
        input:-webkit-autofill,
        textarea:-webkit-autofill,
        select:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 1000px #FFFFFF inset !important;
          -webkit-text-fill-color: #0f3e17 !important;
          caret-color: #0f3e17;
        }
      `}</style>
      <div className="gs-shell-inner">
        <aside className="gs-rail" aria-label="What happens next">
          <p className="gs-rail-brand">byonco</p>
          <p className="gs-rail-lede">
            A quiet intake so we can match you with care that fits your diagnosis, city, and urgency.
          </p>
          <ol className="gs-rail-steps">
            <li>
              <span className="gs-rail-step-num">1</span>
              <div className="gs-rail-step-copy">
                <strong>Share your details</strong>
                <span>Cancer type, stage, cities, and timing. Takes a few minutes.</span>
              </div>
            </li>
            <li>
              <span className="gs-rail-step-num">2</span>
              <div className="gs-rail-step-copy">
                <strong>We keep them on file</strong>
                <span>Your profile follows you into the app. No starting from scratch.</span>
              </div>
            </li>
            <li>
              <span className="gs-rail-step-num">3</span>
              <div className="gs-rail-step-copy">
                <strong>Get clearer next steps</strong>
                <span>Personalized guidance for hospitals, visits, and questions to ask.</span>
              </div>
            </li>
          </ol>
          <p className="gs-rail-foot">Private by design. Built to walk with you between visits.</p>
        </aside>

        <main className="gs-main">
          <div className="gs-back">
            <Button
              variant="ghost"
              className="text-forest-ink hover:text-charcoal px-2"
              onClick={() => navigate('/')}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Button>
          </div>

          <div className="gs-hero">
            <h1>{savedSubmission ? 'Update your care details' : "Let's get you started"}</h1>
            <p>
              {savedSubmission
                ? 'Your previous answers are filled in. Change only what you need, then save.'
                : 'Tell us about the patient and the care you are looking for. We will remember these details for next time.'}
            </p>
          </div>

          <div className="gs-main-card">
            <div className="mb-5">
              <h2 className="text-xl sm:text-2xl text-forest-ink font-semibold" style={{ fontFamily: "'Bricolage Grotesque', 'Instrument Sans', system-ui, sans-serif" }}>
                Patient Information
              </h2>
              <p className="text-[#3d4a42] text-sm sm:text-base mt-1">
                All fields marked with * are required
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                {error && (
                <div className="bg-[#f6eee9] border border-hair text-forest-ink rounded-2xl p-4 text-sm flex items-start gap-2" role="alert">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="gs-section space-y-4">
                  <h3 className="gs-section-title">
                    <User className="w-5 h-5" />
                    Personal Information
                  </h3>
                  
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="full_name" className="text-forest-ink font-normal text-sm sm:text-base">
                        Full Name <span className="text-forest-ink">*</span>
                      </Label>
                      <Input
                        id="full_name"
                        type="text"
                        placeholder="Emily Carter"
                        value={formData.full_name}
                        onChange={(e) => handleChange('full_name', e.target.value)}
                        onBlur={() => handleBlur('full_name')}
                      className="w-full h-12 bg-paper border-hair text-forest-ink rounded-2xl placeholder:text-[#3d4a42] focus-visible:ring-0 focus:border-forest-ink text-sm px-3 sm:px-4 py-2.5 sm:py-3 h-auto text-sm sm:text-base"
                        required
                      />
                      {errors.full_name && (
                        <p className="text-sm text-forest-ink">{errors.full_name}</p>
                      )}
                    </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="email" className="text-forest-ink font-normal text-sm sm:text-base">
                        Email <span className="text-forest-ink">*</span>
                        {isAuthenticated ? ' (from your account)' : ''}
                      </Label>
                      <div className="relative">
                      <Mail className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-forest-ink z-10 pointer-events-none" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@email.com"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          onBlur={() => handleBlur('email')}
                          readOnly={isAuthenticated}
                          aria-readonly={isAuthenticated ? 'true' : undefined}
                        className="w-full h-12 bg-paper border-hair text-forest-ink rounded-2xl placeholder:text-[#3d4a42] focus-visible:ring-0 focus:border-forest-ink text-sm pr-3 sm:pr-4 py-2.5 sm:py-3 h-auto text-sm sm:text-base"
                        style={{ paddingLeft: '2.75rem' }}
                          required
                        />
                      </div>
                      {errors.email && (
                        <p className="text-sm text-forest-ink">{errors.email}</p>
                      )}
                    </div>

                  <div className="space-y-1.5 sm:space-y-2">
                    <Label htmlFor="phone" className="text-forest-ink font-normal text-sm sm:text-base">
                        Phone Number <span className="text-forest-ink">*</span>
                      </Label>
                    <div className={`gs-phone${errors.phone ? ' is-error' : ''}`}>
                      <Select
                        value={formData.country_key || dialKey(selectedCountry.iso, selectedCountry.code)}
                        onValueChange={(value) => {
                          const row = countryCodes.find((c) => dialKey(c.iso, c.code) === value);
                          setFormData({
                            ...formData,
                            country_key: value,
                            country_code: row?.code || '+1',
                          });
                        }}
                      >
                        <SelectTrigger className="gs-phone-code" aria-label="Country code">
                          <span className="gs-phone-iso" aria-hidden="true">{selectedCountry.iso}</span>
                          <span className="gs-phone-dial">{selectedCountry.code}</span>
                        </SelectTrigger>
                        <SelectContent
                          className={`${SELECT_CONTENT} gs-phone-menu`}
                          position="popper"
                          sideOffset={8}
                          style={{ zIndex: 9999, minWidth: 280 }}
                        >
                          {countryCodes.map((country) => (
                            <SelectItem
                              key={dialKey(country.iso, country.code)}
                              value={dialKey(country.iso, country.code)}
                              className={SELECT_ITEM}
                            >
                              <span className="gs-phone-option">
                                <span className="gs-phone-iso">{country.iso}</span>
                                <span className="gs-phone-option-dial">{country.code}</span>
                                <span className="gs-phone-option-name">{country.country}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="gs-phone-divider" aria-hidden="true" />
                      <div className="gs-phone-input-wrap">
                        <Phone className="gs-phone-icon" aria-hidden="true" />
                        <Input
                          id="phone"
                          type="tel"
                          inputMode="tel"
                          placeholder="(555) 123-4567"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          onBlur={() => handleBlur('phone')}
                          className="gs-phone-input"
                          required
                        />
                      </div>
                      </div>
                      {errors.phone && (
                        <p className="text-sm text-forest-ink">{errors.phone}</p>
                      )}
                    </div>

                    <div className="space-y-1.5 sm:space-y-2">
                      <Label htmlFor="city" className="text-forest-ink font-normal text-sm sm:text-base">
                        City <span className="text-forest-ink">*</span>
                      </Label>
                      <div className="relative">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-forest-ink z-10 pointer-events-none" />
                        <Input
                          id="city"
                          type="text"
                          placeholder="Houston"
                          value={formData.city}
                          onChange={(e) => handleChange('city', e.target.value)}
                          onBlur={() => handleBlur('city')}
                        className="w-full h-12 bg-paper border-hair text-forest-ink rounded-2xl placeholder:text-[#3d4a42] focus-visible:ring-0 focus:border-forest-ink text-sm pr-4 py-3 h-auto"
                        style={{ paddingLeft: '3.5rem' }}
                          required
                        />
                      </div>
                      {errors.city && (
                        <p className="text-sm text-forest-ink">{errors.city}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="gs-section space-y-4">
                  <h3 className="gs-section-title">
                    <Stethoscope className="w-5 h-5" />
                    Medical Information
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cancer_type" className="text-forest-ink font-normal">
                        Cancer Type <span className="text-forest-ink">*</span>
                      </Label>
                      <Input
                        id="cancer_type"
                        type="text"
                        placeholder="e.g., Breast Cancer, Lung Cancer"
                        value={formData.cancer_type}
                        onChange={(e) => handleChange('cancer_type', e.target.value)}
                        onBlur={() => handleBlur('cancer_type')}
                      className="w-full h-12 bg-paper border-hair text-forest-ink rounded-2xl placeholder:text-[#3d4a42] focus-visible:ring-0 focus:border-forest-ink text-sm px-4 py-3 h-auto"
                        required
                      />
                      {errors.cancer_type && (
                        <p className="text-sm text-forest-ink">{errors.cancer_type}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cancer_stage" className="text-forest-ink font-normal">
                        Cancer Stage <span className="text-forest-ink">*</span>
                      </Label>
                      <Select
                        value={formData.cancer_stage}
                        onValueChange={(value) => handleChange('cancer_stage', value)}
                      >
                      <SelectTrigger className={SELECT_TRIGGER}>
                        <SelectValue placeholder="Select stage" className="text-forest-ink" />
                        </SelectTrigger>
                      <SelectContent 
                        className={SELECT_CONTENT}
                        position="popper"
                        style={{ zIndex: 9999 }}
                      >
                          {cancerStages.map((stage) => (
                          <SelectItem 
                            key={stage} 
                            value={stage} 
                            className={SELECT_ITEM}
                          >
                              {stage}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.cancer_stage && (
                        <p className="text-sm text-forest-ink">{errors.cancer_stage}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="gs-section space-y-4">
                  <h3 className="gs-section-title">
                    <MapPin className="w-5 h-5" />
                    Journey Planning
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="budget_range" className="text-forest-ink font-normal">
                        Budget Range <span className="text-forest-ink">*</span>
                      </Label>
                      <Select
                        value={formData.budget_range}
                        onValueChange={(value) => {
                          const budgetInfo = getBudgetRangesForCountry(formData.country);
                          handleChange('budget_range', value);
                          handleChange('budget_currency', budgetInfo.currency);
                        }}
                      >
                        <SelectTrigger className={SELECT_TRIGGER}>
                          <SelectValue placeholder="Select budget range" className="text-forest-ink" />
                        </SelectTrigger>
                        <SelectContent 
                          className={SELECT_CONTENT}
                          position="popper"
                          style={{ zIndex: 9999 }}
                        >
                          {getBudgetRangesForCountry(formData.country).ranges.map((range) => (
                            <SelectItem 
                              key={range.value} 
                              value={range.value} 
                              className={SELECT_ITEM}
                            >
                              {range.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.budget_range && (
                        <p className="text-sm text-forest-ink">{errors.budget_range}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="preferred_cities" className="text-forest-ink font-normal">
                        Preferred Treatment Cities <span className="text-forest-ink">*</span>
                      </Label>
                      <Input
                        id="preferred_cities"
                        type="text"
                        placeholder="e.g., Houston, New York, Boston"
                        value={formData.preferred_cities}
                        onChange={(e) => handleChange('preferred_cities', e.target.value)}
                        onBlur={() => handleBlur('preferred_cities')}
                        className="w-full h-12 bg-paper border-hair text-forest-ink rounded-2xl placeholder:text-[#3d4a42] focus-visible:ring-0 focus:border-forest-ink text-sm px-4 py-3 h-auto"
                        required
                      />
                      <p className="text-xs text-[#3d4a42]">
                        Mention 1-3 cities where you'd like to explore treatment options (comma-separated)
                      </p>
                      {errors.preferred_cities && (
                        <p className="text-sm text-forest-ink">{errors.preferred_cities}</p>
                      )}
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="urgency" className="text-forest-ink font-normal">
                        Urgency of treatment <span className="text-forest-ink">*</span>
                      </Label>
                      <Select
                        value={formData.urgency}
                        onValueChange={(value) => handleChange('urgency', value)}
                      >
                        <SelectTrigger className={SELECT_TRIGGER}>
                          <SelectValue placeholder="Select urgency" className="text-forest-ink" />
                        </SelectTrigger>
                        <SelectContent 
                          className={SELECT_CONTENT}
                          position="popper"
                          style={{ zIndex: 9999 }}
                        >
                          <SelectItem value="immediately" className={SELECT_ITEM}>
                            Immediately
                          </SelectItem>
                          <SelectItem value="within_week" className={SELECT_ITEM}>
                            Within a week
                          </SelectItem>
                          <SelectItem value="two_to_three_weeks" className={SELECT_ITEM}>
                            2-3 weeks
                          </SelectItem>
                          <SelectItem value="within_month" className={SELECT_ITEM}>
                            Within a month
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.urgency && (
                        <p className="text-sm text-forest-ink">{errors.urgency}</p>
                      )}
                    </div>
                  </div>
                </div>

              <div className="gs-section space-y-4">
                  <h3 className="gs-section-title">
                    <Shield className="w-5 h-5" />
                    Insurance Information
                  </h3>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="has_insurance"
                      checked={formData.has_insurance}
                      onCheckedChange={(checked) => handleChange('has_insurance', checked)}
                    className="border-forest-ink"
                    />
                    <Label htmlFor="has_insurance" className="text-forest-ink cursor-pointer font-medium">
                      I have health insurance
                    </Label>
                  </div>

                  {formData.has_insurance && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="insurance_provider" className="text-forest-ink font-normal">
                          Insurance Provider <span className="text-forest-ink">*</span>
                        </Label>
                        <Input
                          id="insurance_provider"
                          type="text"
                          placeholder="e.g., Blue Cross Blue Shield, UnitedHealthcare, Aetna"
                          value={formData.insurance_provider}
                          onChange={(e) => handleChange('insurance_provider', e.target.value)}
                          onBlur={() => handleBlur('insurance_provider')}
                        className="w-full h-12 bg-paper border-hair text-forest-ink rounded-2xl placeholder:text-[#3d4a42] focus-visible:ring-0 focus:border-forest-ink text-sm px-4 py-3 h-auto"
                        />
                        {errors.insurance_provider && (
                          <p className="text-sm text-forest-ink">{errors.insurance_provider}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="insurance_policy_number" className="text-forest-ink font-normal">
                          Policy Number
                        </Label>
                        <Input
                          id="insurance_policy_number"
                          type="text"
                          placeholder="Policy number (optional)"
                          value={formData.insurance_policy_number}
                          onChange={(e) => handleChange('insurance_policy_number', e.target.value)}
                        className="w-full h-12 bg-paper border-hair text-forest-ink rounded-2xl placeholder:text-[#3d4a42] focus-visible:ring-0 focus:border-forest-ink text-sm px-4 py-3 h-auto"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Preferences Section */}
              <div className="gs-section space-y-4">
                  <h3 className="gs-section-title">
                    <Clock className="w-5 h-5" />
                    Contact Preferences
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="preferred_contact_method" className="text-forest-ink font-normal">
                        Preferred Contact Method
                      </Label>
                      <Select
                        value={formData.preferred_contact_method}
                        onValueChange={(value) => handleChange('preferred_contact_method', value)}
                      >
                      <SelectTrigger className={SELECT_TRIGGER}>
                        <SelectValue className="text-forest-ink" />
                        </SelectTrigger>
                      <SelectContent 
                        className={SELECT_CONTENT}
                        position="popper"
                        style={{ zIndex: 9999 }}
                      >
                          {contactMethods.map((method) => (
                          <SelectItem 
                            key={method} 
                            value={method.toLowerCase()} 
                            className={SELECT_ITEM}
                          >
                              {method}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="preferred_time" className="text-forest-ink font-normal">
                        Preferred Contact Time
                      </Label>
                      <Select
                        value={formData.preferred_time}
                        onValueChange={(value) => handleChange('preferred_time', value)}
                      >
                      <SelectTrigger className={SELECT_TRIGGER}>
                        <SelectValue placeholder="Select time" className="text-forest-ink" />
                        </SelectTrigger>
                      <SelectContent 
                        className={SELECT_CONTENT}
                        position="popper"
                        style={{ zIndex: 9999 }}
                      >
                          {timePreferences.map((time) => (
                          <SelectItem 
                            key={time} 
                            value={time} 
                            className={SELECT_ITEM}
                          >
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="additional_notes" className="text-forest-ink font-normal">
                      Additional Notes (Optional)
                    </Label>
                    <Textarea
                      id="additional_notes"
                      placeholder="Any additional information you'd like to share..."
                      value={formData.additional_notes}
                      onChange={(e) => handleChange('additional_notes', e.target.value)}
                    className="w-full h-12 bg-paper border-hair text-forest-ink rounded-2xl placeholder:text-[#3d4a42] focus-visible:ring-0 focus:border-forest-ink text-sm min-h-[100px] px-4 py-3"
                      rows={4}
                    />
                  </div>
                </div>

              <div className="gs-section space-y-4">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="agree_to_terms"
                      checked={formData.agree_to_terms}
                      onCheckedChange={(checked) => handleChange('agree_to_terms', checked)}
                    className="mt-1 border-forest-ink"
                      required
                    />
                    <Label htmlFor="agree_to_terms" className="text-forest-ink cursor-pointer text-sm font-medium">
                      I agree to the{' '}
                      <a href="/terms-and-conditions" target="_blank" className="text-forest-ink hover:text-charcoal hover:underline">
                        Terms and Conditions
                      </a>{' '}
                      and{' '}
                      <a href="/privacy" target="_blank" className="text-forest-ink hover:text-charcoal hover:underline">
                        Privacy Policy
                      </a>
                      <span className="text-forest-ink"> *</span>
                    </Label>
                  </div>
                  {errors.agree_to_terms && (
                    <p className="text-sm text-forest-ink">{errors.agree_to_terms}</p>
                  )}
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <Button
                    type="submit"
                  className="w-full bg-forest-ink hover:bg-forest-shadow text-cream-paper py-6 text-lg font-semibold rounded-full"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      savedSubmission ? 'Save updates' : 'Submit & Get Started'
                    )}
                  </Button>
                </div>
              </form>
          </div>
        </main>
      </div>
    </div>
  );
}
